/**
 * Per-project scheduled-job admin model.
 *
 * The Cloud Functions side runs each scheduled trigger (Backlog 同期 /
 * Suggest 生成 / 日次サマリー / Snooze 復帰) at a fixed high-frequency
 * cron set in code (Firebase Functions can't take a Firestore-backed
 * schedule). The per-project document below holds:
 *
 *   - **the user-managed cron** ("毎時 0 分", "0,30 * * * *", …): the
 *     trigger reads it on every tick and self-gates so the handler only
 *     runs at the configured cadence (`nextRunAt <= now`);
 *   - **the enable toggle**: a one-click pause that survives across
 *     deploys without code changes;
 *   - **the run telemetry** (`lastRunAt`, `lastStatus`, `lastError`,
 *     `lastDurationMs`, `lastSummary`): so the Settings page can render
 *     a "3 分前 / 成功 / 12 件処理" badge without scanning history.
 *
 * Run history lives under the `runs` subcollection (see `ScheduledJobRunDoc`).
 */
import type { MemberRolesMap, Timestamp, UserId } from './common.js';

/**
 * Stable ids for the scheduled jobs the system ships with. Adding a new
 * scheduled trigger means appending a new id here AND adding a default
 * entry under `SCHEDULED_JOB_DEFAULTS`.
 */
export const SCHEDULED_JOB_IDS = [
  'backlog-sync',
  'daily-notifications',
  'revive-suggestions',
  'daily-summary',
] as const;
export type ScheduledJobId = (typeof SCHEDULED_JOB_IDS)[number];

/**
 * Outcome enum for both `ScheduledJobDoc.lastStatus` and the terminal
 * state of a `ScheduledJobRunDoc`. `'partial'` means the handler
 * processed some items but at least one failed — useful for
 * fan-out-style jobs (one bad project shouldn't paint the whole tick
 * red).
 */
export type ScheduledJobStatus = 'success' | 'failed' | 'partial';

export interface ScheduledJobDefault {
  /** Logical id — also the doc id under `/projects/{pid}/scheduledJobs`. */
  jobId: ScheduledJobId;
  /** Display name surfaced in the Settings UI. */
  name: string;
  /** One-or-two-line description rendered next to the toggle. */
  description: string;
  /**
   * Default standard 5-field cron expression (minute hour dom month dow).
   * Mirrors the high-frequency tick that ships in the Functions code so
   * un-seeded projects behave identically to seeded ones.
   */
  defaultCronExpression: string;
}

/**
 * Static catalog of known jobs. The seed migration (and the runner's
 * lazy fallback) reads this when bootstrapping `scheduledJobs` docs for
 * legacy projects.
 */
export const SCHEDULED_JOB_DEFAULTS: ReadonlyArray<ScheduledJobDefault> = [
  {
    jobId: 'backlog-sync',
    name: 'Backlog Issue 同期',
    description:
      'Backlog API から最新のステータス・コメントを取得し、ローカルキャッシュを更新します。',
    defaultCronExpression: '*/15 * * * *',
  },
  {
    jobId: 'daily-notifications',
    name: '日次サマリーメール',
    description:
      '当日の担当タスクをメンバーへメール通知します（指定時刻 / JST）。',
    defaultCronExpression: '0 8 * * *',
  },
  {
    jobId: 'revive-suggestions',
    name: 'Snooze 期限切れ復帰',
    description:
      '一定期間 snooze されていた Suggestion を pending に戻します。',
    defaultCronExpression: '*/10 * * * *',
  },
  {
    jobId: 'daily-summary',
    name: '平日朝の Daily Summary (Discord)',
    description:
      '平日 07:00 JST に、前日 (00:00–24:00 JST) のアクティビティを集計して Discord に投稿します。',
    // 平日 (月〜金) 7:00 JST。Functions 側の onSchedule も同じ cron で
    // 起動するので、per-project ゲートはほぼ "毎日 1 回" になる。
    defaultCronExpression: '0 7 * * 1-5',
  },
];

/**
 * `/projects/{pid}/scheduledJobs/{jobId}`.
 *
 * The doc id is the `ScheduledJobId` (no separate jobId field-vs-id
 * mismatch is possible). Member fields are denormalized on every write
 * so security rules don't need to `get()` the parent project (mirrors
 * the rest of the project subcollection conventions — see addendum §2).
 */
export interface ScheduledJobDoc {
  jobId: ScheduledJobId;
  projectId: string;
  name: string;
  description: string;
  cronExpression: string;
  enabled: boolean;
  /** Last time the runner finished a tick for this project. */
  lastRunAt: Timestamp | null;
  /**
   * The cron's "next fire time" computed at the end of the previous
   * run. The trigger uses this to self-gate: if `nextRunAt > now` the
   * handler skips. `null` means "due now" — typically right after a
   * cron change or after seeding.
   */
  nextRunAt: Timestamp | null;
  lastStatus: ScheduledJobStatus | null;
  lastError: string | null;
  lastDurationMs: number | null;
  /** Free-form summary string ("12 issues synced"). Populated by handler. */
  lastSummary: string | null;
  memberUserIds: UserId[];
  memberRoles: MemberRolesMap;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * `/projects/{pid}/scheduledJobs/{jobId}/runs/{runId}`.
 *
 * Created at handler start (status='running'); updated to a terminal
 * status at the end. The runner's catch handler always finalizes — a
 * crashed handler still produces a final `'failed'` doc with the error
 * message, so the UI history view never renders a permanently-running
 * row.
 */
export interface ScheduledJobRunDoc {
  runId: string;
  jobId: ScheduledJobId;
  projectId: string;
  startedAt: Timestamp;
  endedAt: Timestamp | null;
  /**
   * Terminal status when the run is finished. `'running'` is the
   * transient state between handler start and end — UI can show a
   * spinner badge on these rows.
   */
  status: 'running' | ScheduledJobStatus;
  durationMs: number | null;
  itemsProcessed: number | null;
  errorMessage: string | null;
  errorStack: string | null;
  triggeredBy: 'schedule' | 'manual';
  triggeredByUserId: UserId | null;
  summary: string | null;
  memberUserIds: UserId[];
  memberRoles: MemberRolesMap;
}

/**
 * Soft retention cap for the `runs` subcollection. The runner trims
 * older docs after each successful end so a busy project's history
 * doesn't grow unbounded — 100 runs ≈ 25h at 15-min cadence, which
 * comfortably covers the "成功率(直近30日)" graph the UI renders.
 */
export const SCHEDULED_JOB_RUN_RETENTION = 100;

/**
 * "Humanize" a small subset of standard cron expressions — enough to
 * label the rows the seed catalog ships with. Falls back to returning
 * the raw expression for anything we don't recognize so the UI never
 * renders an empty cell. Pure — safe to call on every render.
 */
export function humanizeCronExpression(expr: string): string {
  const trimmed = expr.trim();
  // every-N-minutes — `*/N * * * *`
  const everyN = /^\*\/(\d+) \* \* \* \*$/.exec(trimmed);
  if (everyN) {
    const n = Number(everyN[1]);
    if (n === 1) return '毎分';
    if (n === 60) return '毎時 0 分';
    return `${n} 分ごと`;
  }
  if (trimmed === '* * * * *') return '毎分';
  // every hour at minute M
  const hourM = /^(\d+) \* \* \* \*$/.exec(trimmed);
  if (hourM) {
    return `毎時 ${hourM[1]} 分`;
  }
  // daily at HH:MM
  const dailyHM = /^(\d+) (\d+) \* \* \*$/.exec(trimmed);
  if (dailyHM) {
    const m = String(dailyHM[1]).padStart(2, '0');
    const h = String(dailyHM[2]).padStart(2, '0');
    return `毎日 ${h}:${m}`;
  }
  return trimmed;
}
